import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../../config/axios.config';
import { useReportService, useInvalidateReportService } from '../../../hooks/queries/useReportService';
import GenericSurepassOutputComponent from '../output/GenericSurepassOutputComponent';
import ConsentCheckbox from './ConsentCheckbox';
import ReportBanner from '../ReportBanner';

const REGISTRATION_TYPES = [
    { label: 'Project', value: 'project', icon: 'pi-home', description: 'Real-estate project registered with the RERA authority' },
    { label: 'Agent', value: 'agent', icon: 'pi-user', description: 'Real-estate agent registered with the RERA authority' },
];

// Custom Dropdown item template — shows icon + label + description
const registrationTypeItemTemplate = (option) => {
    if (!option) return null;
    return (
        <div className="flex align-items-center gap-2">
            <i className={`pi ${option.icon} text-primary`} aria-hidden="true" />
            <div className="flex flex-column">
                <span className="text-sm font-medium text-900">{option.label}</span>
                <span className="text-xs text-500">{option.description}</span>
            </div>
        </div>
    );
};

const registrationTypeValueTemplate = (option, props) => {
    if (option) {
        return (
            <div className="flex align-items-center gap-2">
                <i className={`pi ${option.icon} text-primary`} aria-hidden="true" />
                <span className="text-sm">{option.label}</span>
            </div>
        );
    }
    return <span className="text-500 text-sm">{props.placeholder}</span>;
};

const ReraV2InputComponent = () => {
    const { serviceId } = useParams();
    const toast = useRef(null);
    const submittingRef = useRef(false);

    const [formData, setFormData] = useState({
        registration_number: '',
        registration_type: '',
        state_name: '',
        consent: false,
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    // #161: one shared, cached query instead of a per-component fetch on every
    // mount. Concurrent consumers are deduped into a single request and a
    // revisit is served from cache; the balance is invalidated after a report
    // runs (below), which is what keeps a cached credit figure correct.
    const {
        data: fetchedServiceDetails,
        isFetching: fetchingService,
        isError: serviceError,
    } = useReportService(serviceId);
    const invalidateReportService = useInvalidateReportService();
    const serviceDetails = serviceError
        ? { available_credits: 'N/A', credits_required: 'N/A' }
        : (fetchedServiceDetails || null);

    const handleField = (field) => (e) => {
        const raw = e.target ? e.target.value : e.value;
        setFormData((prev) => ({ ...prev, [field]: raw ?? '' }));
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
    };

    const handleConsentChange = (e) => {
        setFormData((prev) => ({ ...prev, consent: e.checked }));
        if (errors.consent) setErrors((prev) => ({ ...prev, consent: '' }));
    };

    const validate = () => {
        const next = {};
        if (!formData.registration_number.trim()) next.registration_number = 'Registration number is required';
        if (!formData.registration_type) next.registration_type = 'Please select a registration type';
        if (!formData.state_name.trim()) next.state_name = 'State name is required';
        if (!formData.consent) next.consent = 'Please confirm consent before submitting';
        return next;
    };

    const submit = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.post('/surepass/rera/rera-v2', {
                registration_number: formData.registration_number.trim(),
                registration_type: formData.registration_type,
                state_name: formData.state_name.trim(),
                api_provider_service_id: parseInt(serviceId, 10),
            });
            if (!res.data.success) throw new Error(res.data.message || 'Verification failed');
            setReportData(res.data);
            toast.current?.show({ severity: 'success', summary: 'RERA verified', detail: 'Report ready below', life: 2000 });
            // Credits just changed. Invalidating updates every mounted consumer at
            // once and is ordered strictly after the deduction.
            invalidateReportService(serviceId);
        } catch (error) {
            console.error('RERA verify failed:', error?.message || 'Unknown error');
            const status = error.response?.status;
            const responseData = error.response?.data;

            // If Surepass returned result data (even on a "failed" verification),
            // display it so the user can see what was returned.
            if (responseData?.data) {
                setReportData(responseData);
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Verification result',
                    detail: responseData.message || 'The RERA record could not be verified',
                    life: 3500,
                });
                return;
            }

            const map = {
                500: 'Internal server error. Please try again later.',
                422: 'Please check the inputs and try again.',
                429: 'Too many requests. Please wait a minute before trying again.',
                400: 'Invalid input or RERA record not found.',
                404: 'No RERA record found for these details.',
            };
            let detail = map[status] || responseData?.message || error.message || 'Verification failed';
            if (status === 402) {
                const c = responseData || {};
                detail = `Insufficient credits. Required: ${c.required_credits ?? 'N/A'}, Available: ${c.current_balance ?? 'N/A'}`;
            }
            toast.current?.show({ severity: 'error', summary: 'Verification failed', detail, life: 3500 });
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const next = validate();
        if (Object.keys(next).length > 0) {
            setErrors(next);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix the highlighted fields', life: 3000 });
            return;
        }
        setErrors({});
        submittingRef.current = true;
        const required = serviceDetails?.credits_required;
        const available = serviceDetails?.available_credits;
        if (typeof available === 'number' && typeof required === 'number' && available < required) {
            submittingRef.current = false;
            toast.current?.show({ severity: 'error', summary: 'Insufficient credits', detail: `Required: ${required}, Available: ${available}`, life: 3500 });
            return;
        }
        confirmDialog({
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">Registration:</span> <span className="text-900 font-medium">{formData.registration_number}</span></div>
                    <div className="mb-2"><span className="text-600">Type:</span> <span className="text-900 font-medium">{formData.registration_type === 'project' ? 'Project' : 'Agent'}</span></div>
                    <div className="mb-2"><span className="text-600">State:</span> <span className="text-900 font-medium">{formData.state_name}</span></div>
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="text-900 font-medium">{available ?? 'N/A'}</span></div>
                    <div><span className="text-600">Credits to deduct:</span> <span className="text-900 font-medium">{required ?? 'N/A'}</span></div>
                </div>
            ),
            header: 'Confirm RERA verification',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, verify',
            rejectLabel: 'Cancel',
            accept: submit,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '450px' },
            draggable: false,
        });
    };

    const handleAnother = () => {
        setReportData(null);
        setFormData({ registration_number: '', registration_type: '', state_name: '', consent: false });
        setErrors({});
    };

    const fieldClass = (f, mono = false) =>
        classNames('w-full p-inputtext-sm', mono && 'font-mono', { 'p-invalid': !!errors[f] });
    const errTip = (f, helpId) => errors[f] && (
        <small id={helpId} className="p-error block mt-1" role="alert">
            <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
            {errors[f]}
        </small>
    );

    const available = serviceDetails?.available_credits;
    const required = serviceDetails?.credits_required;
    const lowCredits = typeof available === 'number' && typeof required === 'number' && available < required;

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />

            <ReportBanner
                icon="pi-home"
                title="RERA V2 Verification"
                subtitle="Verify RERA registration details for projects and agents"
                availableCredits={available}
                requiredCredits={required}
                loading={fetchingService}
            />
            {lowCredits && !fetchingService && (
                <div className="mb-3">
                    <Tag severity="warning" icon="pi pi-exclamation-triangle" value="Low credits — please recharge before verifying" />
                </div>
            )}

            <Card>
                {!reportData ? (
                    <form onSubmit={handleSubmit} noValidate>
                        <div className="grid">
                            <div className="field col-12 md:col-7">
                                <label htmlFor="reraReg" className="block font-medium text-sm mb-2">
                                    Registration Number <span className="text-red-500">*</span>
                                </label>
                                <IconField iconPosition="left" className="w-full">
                                    <InputIcon className="pi pi-hashtag" />
                                    <InputText
                                        id="reraReg"
                                        value={formData.registration_number}
                                        onChange={handleField('registration_number')}
                                        placeholder="e.g. P52100012345"
                                        maxLength={50}
                                        spellCheck={false}
                                        disabled={loading}
                                        className={fieldClass('registration_number', true)}
                                        aria-describedby="reraReg-help"
                                        aria-invalid={!!errors.registration_number}
                                    />
                                </IconField>
                                {errTip('registration_number', 'reraReg-help')}
                            </div>
                            <div className="field col-12 md:col-5">
                                <label htmlFor="reraType" className="block font-medium text-sm mb-2">
                                    Registration Type <span className="text-red-500">*</span>
                                </label>
                                <Dropdown
                                    id="reraType"
                                    value={formData.registration_type}
                                    options={REGISTRATION_TYPES}
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={handleField('registration_type')}
                                    placeholder="Select type"
                                    itemTemplate={registrationTypeItemTemplate}
                                    valueTemplate={registrationTypeValueTemplate}
                                    disabled={loading}
                                    className={fieldClass('registration_type')}
                                    aria-describedby="reraType-help"
                                    aria-invalid={!!errors.registration_type}
                                />
                                {errTip('registration_type', 'reraType-help')}
                            </div>
                            <div className="field col-12 md:col-6">
                                <label htmlFor="reraState" className="block font-medium text-sm mb-2">
                                    State Name <span className="text-red-500">*</span>
                                </label>
                                <IconField iconPosition="left" className="w-full">
                                    <InputIcon className="pi pi-map" />
                                    <InputText
                                        id="reraState"
                                        value={formData.state_name}
                                        onChange={handleField('state_name')}
                                        placeholder="e.g. Maharashtra"
                                        maxLength={50}
                                        autoCapitalize="words"
                                        disabled={loading}
                                        className={fieldClass('state_name')}
                                        aria-describedby="reraState-help"
                                        aria-invalid={!!errors.state_name}
                                    />
                                </IconField>
                                {errTip('state_name', 'reraState-help')}
                            </div>
                        </div>

                        <ConsentCheckbox
                            checked={formData.consent}
                            onChange={handleConsentChange}
                            error={errors.consent}
                            reportType="rera-v2"
                        />

                        <div className="flex justify-content-end mt-3">
                            <Button
                                type="submit"
                                label="Verify RERA"
                                icon="pi pi-arrow-right"
                                iconPos="right"
                                loading={loading}
                                disabled={loading}
                                size="small"
                            />
                        </div>
                    </form>
                ) : (
                    <>
                        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2 mb-3">
                            <div className="flex align-items-center gap-2">
                                {reportData?.success
                                    ? <i className="pi pi-check-circle text-green-500 text-2xl" aria-hidden="true" />
                                    : <i className="pi pi-exclamation-triangle text-orange-500 text-2xl" aria-hidden="true" />
                                }
                                <div>
                                    <p className="text-sm font-semibold text-900 m-0">
                                        {reportData?.success ? 'RERA verified' : 'Verification failed — record not found'}
                                    </p>
                                    <p className="text-xs text-500 m-0">
                                        {formData.registration_number} · {formData.registration_type === 'project' ? 'Project' : 'Agent'} · {formData.state_name}
                                    </p>
                                </div>
                            </div>
                            <Button
                                label="Verify another"
                                icon="pi pi-refresh"
                                severity="secondary"
                                outlined
                                size="small"
                                onClick={handleAnother}
                            />
                        </div>
                        <GenericSurepassOutputComponent reportData={reportData} title="RERA Verification Report" icon="pi-home" />
                    </>
                )}
            </Card>
        </>
    );
};

export default ReraV2InputComponent;
